import { displayedRowStats } from './hrBounds.js';
import { FONT_HEX, FONT_LENGTH, FONT_WIDTHS } from './hrFont.js';
import { BUCKETS, parseStart } from './hrModel.js';
import { cleanFacility } from './repText.js';

const WIDTH = 612;
const HEIGHT = 792;
const PLOT_X = 72;
const PLOT_WIDTH = 468;
const PLOT_HEIGHT = 48;
const FIRST_TOP = 116;
const ROW_PITCH = 78;
const winAnsi = new Map([
  [8364, 128],
  [8218, 130],
  [402, 131],
  [8222, 132],
  [8230, 133],
  [8224, 134],
  [8225, 135],
  [710, 136],
  [8240, 137],
  [352, 138],
  [8249, 139],
  [338, 140],
  [381, 142],
  [8216, 145],
  [8217, 146],
  [8220, 147],
  [8221, 148],
  [8226, 149],
  [8211, 150],
  [8212, 151],
  [732, 152],
  [8482, 153],
  [353, 154],
  [8250, 155],
  [339, 156],
  [382, 158],
  [376, 159],
]);
function bytesForText(value) {
  return Array.from(String(value), (char) => {
    const code = char.codePointAt(0);
    if (code >= 32 && code <= 126) return code;
    if (code >= 160 && code <= 255) return code;
    if (winAnsi.has(code)) return winAnsi.get(code);
    throw new Error(
      'PDF fields support Western European characters. Remove unsupported characters.'
    );
  });
}
export function validateMetadata(meta) {
  parseStart(meta.start);
  for (const key of ['facility', 'patient', 'study', 'gender', 'age'])
    bytesForText(key === 'facility' ? cleanFacility(meta[key]) : meta[key] || '');
}

const textWidth = (text, size) =>
  bytesForText(text).reduce((sum, byte) => sum + (FONT_WIDTHS[byte] * size) / 1000, 0);
function fit(text, size, width) {
  let result = String(text || '');
  if (textWidth(result, size) <= width) return result;
  while (result && textWidth(`${result}...`, size) > width) result = result.slice(0, -1);
  return `${result}...`;
}
function timeLabel(start, elapsed) {
  const time = new Date(start + elapsed * 1000);
  const clock = `${String(time.getUTCHours()).padStart(2, '0')}:${String(time.getUTCMinutes()).padStart(2, '0')}`;
  const day = Math.floor((time.getTime() - Math.floor(start / 86400000) * 86400000) / 86400000) + 1;
  return `${clock}(${day})`;
}

export function reportScene(model, meta) {
  validateMetadata(meta);
  const start = parseStart(meta.start);
  const displayedStats = displayedRowStats(model.rowStats, meta);
  // One common scale across every row, based on the plotted bucket averages.
  const yMax =
    Math.ceil(model.buckets.reduce((max, bucket) => Math.max(max, bucket.mean ?? 0), 150) / 25) *
    25;
  const yRange = yMax - 30;
  const ticks =
    yMax === 150
      ? [60, 90, 120, 150]
      : [1, 2, 3, 4].map((step) =>
          step === 4 ? yMax : Math.round((30 + (yRange * step) / 4) / 25) * 25
        );
  const scene = [];
  const rect = (x, y, w, h, fill = null, stroke = null, lw = 0.45) =>
    scene.push({ kind: 'rect', x, y, w, h, fill, stroke, lw });
  const line = (x, y, x2, y2, stroke = '#000000', lw = 0.45) =>
    scene.push({ kind: 'line', x, y, x2, y2, stroke, lw });
  const text = (x, y, value, size = 8, align = 'left') =>
    scene.push({ kind: 'text', x, y, value: String(value), size, align });
  rect(0, 0, WIDTH, HEIGHT, '#ffffff');
  rect(36, 18, 540, 36, '#e5efff', '#000000');
  text(306, 31, fit(cleanFacility(meta.facility) || 'HR Trend Report', 11, 510), 11, 'center');
  text(306, 47, cleanFacility(meta.facility) ? 'HR Trend Report' : 'HR Study', 11, 'center');
  rect(36, 60, 540, 16, null, '#000000');
  text(39, 71, fit(`Name: ${meta.patient || '-'}`, 8, 190));
  text(235, 71, fit(`Gender: ${meta.gender || '-'}`, 8, 68));
  text(308, 71, fit(`Age: ${meta.age ? `${meta.age} yrs` : '-'}`, 8, 65));
  text(
    572,
    71,
    `Start Date: ${meta.start.slice(5, 7)}/${meta.start.slice(8, 10)}/${meta.start.slice(0, 4)}`,
    8,
    'right'
  );
  rect(36, 82, 540, 18, null, '#000000');
  text(306, 94, fit(`HR${meta.study ? ` - Study ID: ${meta.study}` : ''}`, 8, 510), 8, 'center');
  const bottom = FIRST_TOP + (model.rows - 1) * ROW_PITCH + PLOT_HEIGHT + 18;
  rect(36, 100, 540, bottom - 100, null, '#000000');
  for (let row = 0; row < model.rows; row += 1) {
    const top = FIRST_TOP + row * ROW_PITCH;
    const base = top + PLOT_HEIGHT;
    const rowStart = row * model.rowSeconds;
    text(PLOT_X, top - 5, 'BPM', 7);
    text(306, top - 5, 'HR', 8, 'center');
    const stats = displayedStats[row];
    const bpm = (value) =>
      value === null || value === undefined ? '—' : `${Math.round(value)} bpm`;
    text(116, top - 5, `Avg HR: ${bpm(stats?.average)}`, 7.5);
    text(374, top - 5, `Max HR: ${bpm(stats?.max)}`, 7.5);
    text(466, top - 5, `Min HR: ${bpm(stats?.min)}`, 7.5);
    // Shade local clock 20:00 through 08:00. Keep time arithmetic independent of browser timezone.
    const absStart = start + rowStart * 1000;
    const absEnd = absStart + model.rowSeconds * 1000;
    for (
      let midnight = Math.floor(absStart / 86400000) * 86400000 - 86400000;
      midnight < absEnd;
      midnight += 86400000
    ) {
      const from = Math.max(absStart, midnight + 20 * 3600000);
      const to = Math.min(absEnd, midnight + 32 * 3600000);
      if (to > from)
        rect(
          PLOT_X + ((from - absStart) / (model.rowSeconds * 1000)) * PLOT_WIDTH,
          top,
          ((to - from) / (model.rowSeconds * 1000)) * PLOT_WIDTH,
          PLOT_HEIGHT,
          '#efefef'
        );
    }
    // Connect adjacent bucket averages; missing buckets break the trace.
    let previous = null;
    let connected = false;
    const markIsolated = () => {
      if (previous && !connected) {
        line(previous.x - 0.3, previous.y, previous.x + 0.3, previous.y, '#303030', 0.7);
      }
    };
    for (let i = 0; i < BUCKETS; i += 1) {
      const bucket = model.buckets[row * BUCKETS + i];
      if (!Number.isFinite(bucket?.mean)) {
        markIsolated();
        previous = null;
        connected = false;
        continue;
      }
      const x = PLOT_X + ((i + 0.5) * PLOT_WIDTH) / BUCKETS;
      const hr = Math.max(30, Math.min(yMax, bucket.mean));
      const y = base - ((hr - 30) / yRange) * PLOT_HEIGHT;
      if (previous) {
        line(previous.x, previous.y, x, y, '#303030', 0.7);
        connected = true;
      }
      previous = { x, y };
    }
    markIsolated();
    // Mark each visible missing-data span at its bucket boundaries. Do not
    // mark unused space after the recording ends as a missing-data gap.
    const rowEnd = Math.min(
      rowStart + model.rowSeconds,
      model.duration ?? model.rows * model.rowSeconds
    );
    const bucketSeconds = model.rowSeconds / BUCKETS;
    let gapStart = null;
    const markGap = (end) => {
      const left = PLOT_X + ((gapStart - rowStart) / model.rowSeconds) * PLOT_WIDTH;
      const right = PLOT_X + ((end - rowStart) / model.rowSeconds) * PLOT_WIDTH;
      line(left, top, left, base, '#000000', 0.6);
      line(right, top, right, base, '#000000', 0.6);
    };
    for (let i = 0; i < BUCKETS && rowStart + i * bucketSeconds < rowEnd; i += 1) {
      const time = rowStart + i * bucketSeconds;
      const missing = !Number.isFinite(model.buckets[row * BUCKETS + i]?.mean);
      if (missing && gapStart === null) gapStart = time;
      if (!missing && gapStart !== null) {
        markGap(time);
        gapStart = null;
      }
    }
    if (gapStart !== null) markGap(rowEnd);
    rect(PLOT_X, top, PLOT_WIDTH, PLOT_HEIGHT, null, '#000000');
    for (const hr of ticks)
      text(PLOT_X - 3, base - ((hr - 30) / yRange) * PLOT_HEIGHT + 2.6, hr, 8, 'right');
    for (let tick = 0; tick <= 24; tick += 1) {
      const x = PLOT_X + (tick / 24) * PLOT_WIDTH;
      line(x, base, x, base + (tick % 4 === 0 ? 5 : 3));
      if (tick % 4 === 0)
        text(
          x,
          base + 13,
          timeLabel(start, rowStart + (tick / 24) * model.rowSeconds),
          7.5,
          'center'
        );
    }
  }
  text(576, 778, '1 / 1', 7, 'right');
  return scene;
}
const xml = (s) =>
  String(s)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
export function sceneSvg(scene) {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 612 792" width="816" height="1056" role="img" aria-label="HR study report">${scene
    .map((p) => {
      if (p.kind === 'rect')
        return `<rect x="${p.x}" y="${p.y}" width="${p.w}" height="${p.h}" fill="${p.fill || 'none'}" stroke="${p.stroke || 'none'}" stroke-width="${p.lw}"/>`;
      if (p.kind === 'line')
        return `<line x1="${p.x}" y1="${p.y}" x2="${p.x2}" y2="${p.y2}" stroke="${p.stroke}" stroke-width="${p.lw}"/>`;
      return `<text x="${p.x}" y="${p.y}" font-family="Arial, Helvetica, sans-serif" font-size="${p.size}" text-anchor="${p.align === 'center' ? 'middle' : p.align === 'right' ? 'end' : 'start'}">${xml(p.value)}</text>`;
    })
    .join('')}</svg>`;
}
const number = (value) => Number(value.toFixed(4)).toString();
const rgb = (hex) =>
  [1, 3, 5].map((i) => number(parseInt(hex.slice(i, i + 2), 16) / 255)).join(' ');
// Small dependency-free, single-page vector PDF serializer. Only trusted scene primitives become PDF operators.
export function scenePdf(scene) {
  const commands = ['q'];
  for (const p of scene) {
    if (p.kind === 'text') {
      const width = textWidth(p.value, p.size);
      const x = p.x - (p.align === 'center' ? width / 2 : p.align === 'right' ? width : 0);
      const hex = bytesForText(p.value)
        .map((v) => v.toString(16).padStart(2, '0'))
        .join('');
      commands.push(
        `0 g BT /F1 ${number(p.size)} Tf 1 0 0 1 ${number(x)} ${number(HEIGHT - p.y)} Tm <${hex}> Tj ET`
      );
    } else if (p.kind === 'line')
      commands.push(
        `${rgb(p.stroke)} RG ${number(p.lw)} w ${number(p.x)} ${number(HEIGHT - p.y)} m ${number(p.x2)} ${number(HEIGHT - p.y2)} l S`
      );
    else {
      if (p.fill) commands.push(`${rgb(p.fill)} rg`);
      if (p.stroke) commands.push(`${rgb(p.stroke)} RG ${number(p.lw)} w`);
      commands.push(
        `${number(p.x)} ${number(HEIGHT - p.y - p.h)} ${number(p.w)} ${number(p.h)} re ${p.fill && p.stroke ? 'B' : p.fill ? 'f' : 'S'}`
      );
    }
  }
  commands.push('Q');
  const stream = commands.join('\n');
  const objects = [
    '<< /Type /Catalog /Pages 2 0 R >>',
    '<< /Type /Pages /Kids [3 0 R] /Count 1 >>',
    '<< /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Resources << /Font << /F1 4 0 R >> >> /Contents 5 0 R >>',
    `<< /Type /Font /Subtype /TrueType /BaseFont /DejaVuSans /Encoding /WinAnsiEncoding /FirstChar 32 /LastChar 255 /Widths [${FONT_WIDTHS.slice(32).join(' ')}] /FontDescriptor 6 0 R >>`,
    `<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`,
    '<< /Type /FontDescriptor /FontName /DejaVuSans /Flags 32 /FontBBox [-1021 -463 1793 1232] /ItalicAngle 0 /Ascent 928 /Descent -236 /CapHeight 729 /StemV 80 /FontFile2 7 0 R >>',
    `<< /Length ${FONT_HEX.length + 1} /Length1 ${FONT_LENGTH} /Filter /ASCIIHexDecode >>\nstream\n${FONT_HEX}>\nendstream`,
  ];
  let pdf = '%PDF-1.4\n';
  const offsets = [0];
  objects.forEach((obj, i) => {
    offsets.push(pdf.length);
    pdf += `${i + 1} 0 obj\n${obj}\nendobj\n`;
  });
  const xref = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  offsets.slice(1).forEach((offset) => {
    pdf += `${String(offset).padStart(10, '0')} 00000 n \n`;
  });
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF\n`;
  return new TextEncoder().encode(pdf);
}
